import type {
  CreateSourceRequest,
  CreateSourceResponse,
  Paginated,
  Source,
  SourceDetail,
  SourcePaginationParams,
  ToggleSourceStatusRequest,
  UpdateSourceRequest,
} from '../types/sources.types';
import type { IHttpResponse } from '@/core/api/HttpClient';
import httpClient from '@/core/api/createAxiosHttpClient';

export const sourcesApi = {
  /**
   * Get paginated list of sources
   * GET /api/admin/sources
   * Auth: SuperAdmin required
   */
  getSources: async (
    pagination: SourcePaginationParams = {},
  ): Promise<IHttpResponse<Paginated<Source>>> => {
    const {
      page = 1,
      pageSize = 10,
      searchTerm,
      sortBy = 'name',
      sortAscending = true,
    } = pagination;

    return httpClient.get<Paginated<Source>>('/admin/sources', {
      params: {
        Page: page,
        PageSize: pageSize,
        ...(searchTerm && { SearchTerm: searchTerm }),
        ...(sortBy && { SortBy: sortBy }),
        SortAscending: sortAscending,
      },
    });
  },

  /**
   * Create a new information source
   * POST /api/admin/sources
   * Auth: SuperAdmin required
   *
   * @param data - Source configuration with all required fields based on type
   * @returns Created source response (without type field in response)
   */
  getSourceById: async (id: string): Promise<IHttpResponse<SourceDetail>> => {
    return httpClient.get<SourceDetail>(`/admin/sources/${id}`);
  },

  createSource: async (data: CreateSourceRequest): Promise<IHttpResponse<CreateSourceResponse>> => {
    return httpClient.post<CreateSourceRequest, CreateSourceResponse>('/admin/sources', data);
  },

  /**
   * Update an existing information source
   * PUT /api/admin/sources/{sourceId}
   * Auth: SuperAdmin required
   */
  updateSource: async (id: string, data: UpdateSourceRequest): Promise<void> => {
    await httpClient.put(`/admin/sources/${id}`, data);
  },

  toggleSourceStatus: async (id: string, data: ToggleSourceStatusRequest): Promise<void> => {
    await httpClient.patch(`/admin/sources/${id}/toggle`, data);
  },

  deleteSource: async (id: string): Promise<void> => {
    await httpClient.delete(`/admin/sources/${id}`, {
      reason: 'Fuente obsoleta o ya no necesaria',
    });
  },
};
